<!-- ========== 档口库存管理页面 ========== -->
<template>
  <div class="inventory-management">
    <div class="page-header">
      <h1>{{ $t('stall.inventory') }}</h1>
      <p class="subtitle">管理和查询所有{{ $t('product.stock') }}{{ $t('navigation.products') }}</p>
    </div>
    
    <!-- 搜索和筛选 -->
    <div class="search-section">
      <div class="search-form">
        <div class="form-group">
          <label>{{ $t('navigation.products') }}名称</label>
          <input v-model="filters.productName" type="text" class="form-control" :placeholder="$t('inventory.productName')" @keyup.enter="searchInventory" />
        </div>
        <div class="form-group">
          <label>{{ $t('navigation.products') }}编码</label>
          <input v-model="filters.productCode" type="text" class="form-control" :placeholder="$t('inventory.productCode')" @keyup.enter="searchInventory" />
        </div>
        <div class="form-group">
          <label>{{ $t('product.category') }}</label>
          <select v-model="filters.categoryId" class="form-control">
            <option value="">{{ $t('common.all') }}{{ $t('product.category') }}</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t('product.stock') }}{{ $t('common.status') }}</label>
          <select v-model="filters.stockStatus" class="form-control">
            <option value="">{{ $t('common.all') }}{{ $t('common.status') }}</option>
            <option value="normal">正常{{ $t('product.stock') }}</option>
            <option value="low">低{{ $t('product.stock') }}预警</option>
            <option value="out">{{ $t('product.outOfStock') }}</option>
          </select>
        </div>
        <button class="btn btn-primary" @click="searchInventory">
          <i class="icon-search"></i> {{ $t("inventory.search") }}
        </button>
        <button class="btn btn-secondary" @click="resetFilters">{{ $t('common.reset') }}</button>
      </div>
      
      <div class="actions">
        <button class="btn btn-primary" @click="showNewProductModal">
          <i class="icon-plus"></i> 新增{{ $t('navigation.products') }}
        </button>
        <button class="btn btn-secondary" @click="exportInventory">
          <i class="icon-download"></i> {{ $t("inventory.export") }}
        </button>
      </div>
    </div>
    
    <!-- 库存统计 -->
    <div class="statistics-bar">
      <div class="stat-item">
        <span class="label">总{{ $t('navigation.products') }}数：</span>
        <span class="value">{{ totalProducts }} {{ $t("inventory.items") }}</span>
      </div>
      <div class="stat-item">
        <span class="label">总{{ $t('product.stock') }}值：</span>
        <span class="value">¥{{ totalInventoryValue }}</span>
      </div>
      <div class="stat-item">
        <span class="label">低{{ $t('product.stock') }}{{ $t('navigation.products') }}：</span>
        <span class="value warning">{{ lowStockCount }} {{ $t("inventory.items") }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t('product.outOfStock') }}{{ $t('navigation.products') }}：</span>
        <span class="value danger">{{ outOfStockCount }} 件</span>
      </div>
    </div>
    
    <!-- 库存列表 -->
    <div class="table-container">
      <table class="inventory-table">
        <thead>
          <tr>
            <th width="60">{{ $t("inventory.seq") }}</th>
            <th width="120">{{ $t('navigation.products') }}编码</th>
            <th width="150">{{ $t('navigation.products') }}名称</th>
            <th width="100">{{ $t('product.category') }}</th>
            <th width="80">当前{{ $t('product.stock') }}</th>
            <th width="80">安全{{ $t('product.stock') }}</th>
            <th width="100">{{ $t("inventory.purchasePrice") }}</th>
            <th width="100">{{ $t("inventory.salePrice") }}</th>
            <th width="100">{{ $t('product.stock') }}{{ $t('common.status') }}</th>
            <th width="150">{{ $t('common.action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="inventoryList.length === 0">
            <td colspan="10" class="empty-cell">
              <div class="empty-state">
                <i class="icon-inbox"></i>
                <p>暂无{{ $t('product.stock') }}{{ $t('navigation.products') }}</p>
              </div>
            </td>
          </tr>
          <tr v-for="(item, index) in inventoryList" :key="item.id">
            <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
            <td>{{ item.productCode }}</td>
            <td>
              <div class="product-cell">
                <div class="product-name">{{ item.productName }}</div>
              </div>
            </td>
            <td>{{ getCategoryName(item.categoryId) }}</td>
            <td class="stock-value" :class="getStockClass(item.currentStock, item.safeStock)">
              {{ item.currentStock }}
            </td>
            <td>{{ item.safeStock }}</td>
            <td>¥{{ item.purchasePrice }}</td>
            <td>¥{{ item.salePrice }}</td>
            <td>
              <span :class="['status', `status-${getStockStatus(item.currentStock, item.safeStock)}`]">
                {{ getStockStatusLabel(item.currentStock, item.safeStock) }}
              </span>
            </td>
            <td>
              <button class="btn-action" @click="viewProductDetail(item.id)">
                <i class="icon-eye"></i> {{ $t("inventory.view") }}
              </button>
              <button class="btn-action" @click="editProduct(item.id)">
                <i class="icon-edit"></i> {{ $t('common.edit') }}
              </button>
              <button class="btn-action primary" @click="adjustStock(item.id)">
                <i class="icon-package"></i> {{ $t("inventory.adjust") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <div class="pagination-info">
          {{ totalCount }} {{ $t("pagination.records") }}，
          <select v-model.number="pageSize" @change="searchInventory" class="page-size-select">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div class="pagination-controls">
          <button :disabled="currentPage === 1" @click="currentPage--" class="btn-page">
            <i class="icon-chevron-left"></i> {{ $t("pagination.prev") }}
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="currentPage++" class="btn-page">
            {{ $t("pagination.next") }} <i class="icon-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- 新增/编辑商品模态框 -->
    <div v-if="showProductModal" class="modal-overlay" @click="closeProductModal">
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h2>{{ productFormTitle }}</h2>
          <button class="btn-close" @click="closeProductModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitProduct">
            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('navigation.products') }}名称 <span class="required">*</span></label>
                <input v-model="productForm.productName" type="text" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line170_placeholder')" />
              </div>
              <div class="form-group">
                <label>{{ $t('navigation.products') }}编码 <span class="required">*</span></label>
                <input v-model="productForm.productCode" type="text" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line174_placeholder')" />
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('product.category') }} <span class="required">*</span></label>
                <select v-model="productForm.categoryId" class="form-control" required>
                  <option value="">{{ $t('common.select') }}{{ $t('product.category') }}</option>
                  <option v-for="category in categories" :key="category.id" :value="category.id">
                    {{ category.name }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>{{ $t('product.unit') }}</label>
                <input v-model="productForm.unit" type="text" class="form-control" :placeholder="$t('Stall.Inventory.Index.line190_placeholder')" />
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('Stall.Inventory.Index.line196_text') }} <span class="required">*</span></label>
                <input v-model.number="productForm.purchasePrice" type="number" step="0.0001" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line197_placeholder')" />
              </div>
              <div class="form-group">
                <label>{{ $t('Stall.Inventory.Index.line200_text') }} <span class="required">*</span></label>
                <input v-model.number="productForm.salePrice" type="number" step="0.0001" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line201_placeholder')" />
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>当前{{ $t('product.stock') }} <span class="required">*</span></label>
                <input v-model.number="productForm.currentStock" type="number" min="0" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line208_placeholder')" />
              </div>
              <div class="form-group">
                <label>安全{{ $t('product.stock') }} <span class="required">*</span></label>
                <input v-model.number="productForm.safeStock" type="number" min="0" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line212_placeholder')" />
              </div>
            </div>
            
            <div class="form-group">
              <label>{{ $t('product.description') }}</label>
              <textarea v-model="productForm.description" class="form-control" rows="3" :placeholder="$t('Stall.Inventory.Index.line218_placeholder')"></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closeProductModal">{{ $t('common.cancel') }}</button>
              <button type="submit" class="btn btn-primary">{{ productForm.id ? '更新' : '创建' }}商品</button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- 库存调整模态框 -->
    <div v-if="showStockModal" class="modal-overlay" @click="closeStockModal">
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h2>调整库存 - {{ stockForm.productName }}</h2>
          <button class="btn-close" @click="closeStockModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitStockAdjustment">
            <div class="form-group">
              <label>当前库存：{{ stockForm.currentStock }} {{ stockForm.unit }}</label>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('Stall.Inventory.Index.line245_text') }} <span class="required">*</span></label>
                <select v-model="stockForm.adjustType" class="form-control" required>
                  <option value="add">增加{{ $t('product.stock') }}</option>
                  <option value="reduce">减少{{ $t('product.stock') }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>调整{{ $t('common.quantity') }} <span class="required">*</span></label>
                <input v-model.number="stockForm.quantity" type="number" min="1" class="form-control" required :placeholder="$t('Stall.Inventory.Index.line253_placeholder')" />
              </div>
            </div>
            
            <div class="form-group">
              <label>{{ $t('Stall.Inventory.Index.line258_text') }} <span class="required">*</span></label>
              <select v-model="stockForm.reason" class="form-control" required>
                <option value="">{{ $t('common.select') }}原因</option>
                <option value="purchase">采购{{ $t('afterSales.restock') }}</option>
                <option value="return">退货{{ $t('afterSales.restock') }}</option>
                <option value="adjustment">{{ $t('product.stock') }}调整</option>
                <option value="damage">{{ $t('navigation.products') }}损坏</option>
                <option value="loss">{{ $t('navigation.products') }}丢失</option>
                <option value="sale">{{ $t('Stall.Inventory.Index.line266_text') }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>{{ $t('common.remark') }}</label>
              <textarea v-model="stockForm.remarks" class="form-control" rows="2" :placeholder="$t('Stall.Inventory.Index.line272_placeholder')"></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closeStockModal">{{ $t('common.cancel') }}</button>
              <button type="submit" class="btn btn-primary">{{ $t('common.confirm') }}调整</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'InventoryManagement',
  data() {
    return {
      filters: {
        productName: '',
        productCode: '',
        categoryId: '',
        stockStatus: ''
      },
      inventoryList: [],
      categories: [],
      showProductModal: false,
      showStockModal: false,
      productForm: {
        id: null,
        productName: '',
        productCode: '',
        categoryId: '',
        unit: '件',
        purchasePrice: 0,
        salePrice: 0,
        currentStock: 0,
        safeStock: 0,
        description: ''
      },
      stockForm: {
        id: null,
        productName: '',
        unit: '件',
        currentStock: 0,
        adjustType: 'add',
        quantity: 1,
        reason: '',
        remarks: ''
      },
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      totalProducts: 0,
      totalInventoryValue: '0.00',
      lowStockCount: 0,
      outOfStockCount: 0
    };
  },
  computed: {
    productFormTitle() {
      return this.productForm.id ? '编辑商品' : '新增商品';
    }
  },
  methods: {
    ...mapActions('stall', [
      'getInventory',
      'getCategories',
      'createProduct',
      'updateProduct',
      'deleteProduct',
      'getProductDetail',
      'adjustStock'
    ]),
    
    async loadInventory() {
      try {
        const response = await this.getInventory({
          ...this.filters,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        this.inventoryList = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        this.calculateStatistics();
      } catch (error) {
        this.$message.error(this.$t('inventory.loadFailed'));
      }
    },
    
    async loadCategories() {
      try {
        this.categories = await this.getCategories();
      } catch (error) {
        console.error('加载分类失败', error);
      }
    },
    
    calculateStatistics() {
      if (this.inventoryList.length === 0) {
        this.totalProducts = 0;
        this.totalInventoryValue = '0.00';
        this.lowStockCount = 0;
        this.outOfStockCount = 0;
        return;
      }
      
      let totalValue = 0;
      let lowStock = 0;
      let outOfStock = 0;
      
      this.inventoryList.forEach(item => {
        totalValue += item.currentStock * item.purchasePrice;
        if (item.currentStock === 0) {
          outOfStock++;
        } else if (item.currentStock <= item.safeStock) {
          lowStock++;
        }
      });
      
      this.totalProducts = this.inventoryList.length;
      this.totalInventoryValue = totalValue.toFixed(2);
      this.lowStockCount = lowStock;
      this.outOfStockCount = outOfStock;
    },
    
    searchInventory() {
      this.currentPage = 1;
      this.loadInventory();
    },
    
    resetFilters() {
      this.filters = {
        productName: '',
        productCode: '',
        categoryId: '',
        stockStatus: ''
      };
      this.currentPage = 1;
      this.loadInventory();
    },
    
    showNewProductModal() {
      this.productForm = {
        id: null,
        productName: '',
        productCode: '',
        categoryId: '',
        unit: '件',
        purchasePrice: 0,
        salePrice: 0,
        currentStock: 0,
        safeStock: 0,
        description: ''
      };
      this.showProductModal = true;
    },
    
    closeProductModal() {
      this.showProductModal = false;
    },
    
    async editProduct(productId) {
      try {
        const product = await this.getProductDetail(productId);
        this.productForm = { ...product };
        this.showProductModal = true;
      } catch (error) {
        this.$message.error('加载商品详情失败');
      }
    },
    
    async submitProduct() {
      try {
        if (this.productForm.id) {
          await this.updateProduct(this.productForm);
          this.$message.success('商品已更新');
        } else {
          await this.createProduct(this.productForm);
          this.$message.success('商品已创建');
        }
        this.closeProductModal();
        this.loadInventory();
      } catch (error) {
        this.$message.error('操作失败');
      }
    },
    
    async adjustStock(productId) {
      const product = this.inventoryList.find(item => item.id === productId);
      if (product) {
        this.stockForm = {
          id: productId,
          productName: product.productName,
          unit: product.unit || '件',
          currentStock: product.currentStock,
          adjustType: 'add',
          quantity: 1,
          reason: '',
          remarks: ''
        };
        this.showStockModal = true;
      }
    },
    
    closeStockModal() {
      this.showStockModal = false;
    },
    
    async submitStockAdjustment() {
      try {
        await this.adjustStock({
          productId: this.stockForm.id,
          adjustType: this.stockForm.adjustType,
          quantity: this.stockForm.quantity,
          reason: this.stockForm.reason,
          remarks: this.stockForm.remarks
        });
        this.$message.success('库存已调整');
        this.closeStockModal();
        this.loadInventory();
      } catch (error) {
        this.$message.error('调整库存失败');
      }
    },
    
    viewProductDetail(productId) {
      this.$router.push(`/stall/inventory/${productId}`);
    },
    
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.name : '未知分类';
    },
    
    getStockStatus(currentStock, safeStock) {
      if (currentStock === 0) return 'out';
      if (currentStock <= safeStock) return 'low';
      return 'normal';
    },
    
    getStockStatusLabel(currentStock, safeStock) {
      const status = this.getStockStatus(currentStock, safeStock);
      const labels = {
        normal: '正常',
        low: '低库存',
        out: '缺货'
      };
      return labels[status] || '未知';
    },
    
    getStockClass(currentStock, safeStock) {
      const status = this.getStockStatus(currentStock, safeStock);
      return {
        'stock-normal': status === 'normal',
        'stock-low': status === 'low',
        'stock-out': status === 'out'
      };
    },
    
    exportInventory() {
      this.$message.success(this.$t('inventory.exporting'));
    }
  },
  
  mounted() {
    this.loadCategories();
    this.loadInventory();
  }
};
</script>

<style scoped lang="scss">
.inventory-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  
  .page-header {
    margin-bottom: 20px;
    h1 {
      font-size: 24px;
      font-weight: bold;
      color: #2c3e50;
      margin: 0 0 8px 0;
    }
    .subtitle {
      color: #999;
      font-size: 13px;
      margin: 0;
    }
  }
  
  // 搜索和筛选
  .search-section {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    .search-form {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;
      margin-bottom: 16px;
      
      .form-group {
        display: flex;
        flex-direction: column;
        label {
          font-size: 12px;
          font-weight: 500;
          color: #666;
          margin-bottom: 4px;
        }
        .form-control {
          padding: 8px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: inherit;
          &:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
          }
        }
      }
      button {
        align-self: flex-end;
      }
    }
    
    .actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      button {
        min-width: 120px;
      }
    }
  }
  
  // 统计栏
  .statistics-bar {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
    
    .stat-item {
      background: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      
      .label {
        font-size: 12px;
        color: #999;
        margin-right: 8px;
      }
      .value {
        font-size: 16px;
        font-weight: bold;
        color: #3498db;
        &.warning {
          color: #f39c12;
        }
        &.danger {
          color: #e74c3c;
        }
      }
    }
  }
  
  // 表格容器
  .table-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    .inventory-table {
      width: 100%;
      border-collapse: collapse;
      
      thead {
        background-color: #f5f7fa;
        border-bottom: 2px solid #ddd;
        th {
          padding: 12px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          color: #666;
        }
      }
      
      tbody {
        tr {
          border-bottom: 1px solid #eee;
          transition: background-color 0.3s ease;
          
          &:hover {
            background-color: #f9f9f9;
          }
          
          td {
            padding: 12px;
            font-size: 12px;
            color: #666;
            
            &.empty-cell {
              padding: 0;
            }
            
            &.stock-value {
              font-weight: 600;
              &.stock-normal {
                color: #2ecc71;
              }
              &.stock-low {
                color: #f39c12;
              }
              &.stock-out {
                color: #e74c3c;
              }
            }
            
            .status {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 3px;
              font-size: 11px;
              font-weight: 500;
              
              &.status-normal {
                background-color: #d4edda;
                color: #155724;
              }
              &.status-low {
                background-color: #fff3cd;
                color: #856404;
              }
              &.status-out {
                background-color: #f8d7da;
                color: #721c24;
              }
            }
            
            .product-cell {
              .product-name {
                font-weight: 500;
                color: #2c3e50;
              }
            }
          }
        }
      }
    }
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      i {
        font-size: 48px;
        color: #ddd;
        margin-bottom: 16px;
      }
      p {
        color: #999;
        font-size: 14px;
        margin: 0;
      }
    }
  }
  
  // 表格操作按钮
  .btn-action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    margin-right: 4px;
    background: none;
    border: none;
    color: #3498db;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      color: #2980b9;
    }
    
    &.primary {
      color: #2ecc71;
      &:hover {
        color: #27ae60;
      }
    }
    
    i {
      font-size: 13px;
    }
  }
  
  // 分页
  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-top: 1px solid #eee;
    background: white;
    
    .pagination-info {
      font-size: 12px;
      color: #666;
      .page-size-select {
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin: 0 6px;
        font-size: 12px;
        background: white;
      }
    }
    
    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 12px;
      .btn-page {
        padding: 6px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover:not(:disabled) {
          border-color: #3498db;
          color: #3498db;
        }
        
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
      .page-info {
        font-size: 12px;
        color: #666;
        min-width: 60px;
        text-align: center;
      }
    }
  }
  
  // 模态框
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    
    .modal-dialog {
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
        .btn-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          &:hover {
            color: #2c3e50;
          }
        }
      }
      
      .modal-body {
        padding: 20px;
        form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          
          .form-group {
            display: flex;
            flex-direction: column;
            label {
              font-size: 12px;
              font-weight: 500;
              color: #666;
              margin-bottom: 6px;
              .required {
                color: #e74c3c;
              }
            }
            .form-control {
              padding: 10px 12px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 12px;
              font-family: inherit;
              &:focus {
                outline: none;
                border-color: #3498db;
                box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
              }
            }
          }
          
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          
          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 20px;
            button {
              min-width: 100px;
            }
          }
        }
      }
    }
  }
}
</style>